import { matchRoutes, Outlet, useBlocker, useLocation, useNavigate } from "react-router-dom";
import UserLayout from "./components/Layout/User";
import { ErrorBoundary } from "@sentry/react";
import { markAppError } from "helpers/readyFlag";
import UserFallback from "pages/Fallback/User";

import Swal from "sweetalert2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import router from "router";

import { useDispatch, useSelector } from "react-redux";
import { fetchMenus, fetchUser, getIsDirty, getMenus, setIsDirty } from "store/userSlice";

import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import sanitize from "helpers/sanitize";
import { doLogout } from "helpers/logout";
import { leaveConfirm } from "helpers/common";
import { useTranslation } from "translation/useTranslation";
import { fetchStateEmployeeSettings } from "store/stateEmployeeSlice";
import Link from "components/ui/Link";
import { LICENSE_STATE_UPDATED_EVENT, getLicenseState, setLicenseState as persistLicenseState } from "helpers/licenseHealth";
import { initBuglogForAuthenticatedUser } from "helpers/buglog";
import { Button } from "components/ui";

const exceptionRoutes = [
  '/profile-settings',
  '/initial-setup',
  '/initial-setup/import-sources',
  '/initial-setup/login-users',
  '/initial-setup/roles',
  '/initial-setup/email',
  'period-selector',
  '/settings/login-users',
  '/about'
];

const LICENSE_WARNING_MESSAGES = {
  license_expired_in_grace: 'Your license has expired, but you are currently in a grace period. Please renew your license to continue uninterrupted service.',
  max_employees_exceeded_in_grace: 'Your employee count exceeds the licensed limit. You are currently in a grace period. Please contact sales to upgrade your license.',
};

const DEFAULT_LICENSE_HEALTH_POLL_MS = 5 * 60 * 1000;
const MIN_LICENSE_HEALTH_POLL_MS = 60 * 1000;

function normalizeLicenseHealthPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const health = String(payload.health || '').toLowerCase();
  const warningCode = payload.last_reason || null;
  const warningMessage = warningCode ? (LICENSE_WARNING_MESSAGES[warningCode] || null) : null;

  return {
    status: health || 'valid',
    mode: payload.mode || 'connected',
    staleEnforced: Boolean(payload.stale_enforced),
    offlineOnly: Boolean(payload.offline_only),
    warningCode,
    warningMessage,
    warningCount: payload.warning ? 1 : 0,
    updatedAt: new Date().toISOString(),
  };
}

function useLeaveConfirm(shouldBlock) {
  // Block browser unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  // Block internal navigation
  const blocker = useBlocker(({ currentLocation, nextLocation }) => shouldBlock && currentLocation.pathname !== nextLocation.pathname);

  return blocker;
}

export default function Protected() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const menus = useSelector(getMenus);

  const { editableTranslate } = useTranslation();

  const [allowed, setAllowed] = useState(false);
  const [authVerified, setAuthVerified] = useState(false);
  const [licenseState, setLicenseState] = useState(() => getLicenseState());
  const [licenseHealthPollMs, setLicenseHealthPollMs] = useState(DEFAULT_LICENSE_HEALTH_POLL_MS);
  const isLicenseHealthLoading = useRef(false);

  const [helps, setHelps] = useState([]);

  const globalIsDirty = useSelector(getIsDirty);

  const onError = () => {
    document.body.style.overflowY = 'auto';
    Swal.close();
    markAppError();
  };

  const blocker = useLeaveConfirm(globalIsDirty);

  useEffect(() => {
    const handler = (event) => {
      setLicenseState(event?.detail || getLicenseState());
    };

    window.addEventListener(LICENSE_STATE_UPDATED_EVENT, handler);

    return () => {
      window.removeEventListener(LICENSE_STATE_UPDATED_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    if (blocker?.state === 'blocked') {
      leaveConfirm()
        .then(confirmed => {
          if (confirmed) {
            dispatch(setIsDirty(false));
            blocker.proceed();
          } else {
            blocker.reset();
          }
        });
    }
  }, [blocker]);

  useEffect(() => {
    async function fetchData() {
      try {
        let response = await dispatch(fetchUser()).unwrap();
        setAuthVerified(true);
        if (response.rps) {
          navigate('/period-selector');
          return;
        }
      } catch (err) {
        if (err?.statusCode === 401) {
          handleLogout();
          return;
        }
      }
      dispatch(fetchMenus());
      dispatch(fetchStateEmployeeSettings());
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!authVerified || !allowed) {
      return;
    }

    initBuglogForAuthenticatedUser();
  }, [authVerified, allowed]);

  useEffect(() => {
    // check route permission
    const routes = matchRoutes(router, location);
    const { route } = routes.pop();

    const currentPath = location.pathname.split('/').slice(0, 3).join('/').replace(/\/$/, '');

    if (exceptionRoutes.includes(currentPath)) {
      setAllowed(true);
    } else if (!menus) {
      navigate('/profile-settings');
      return;
    } else if (menus.length > 0) {
      let found = null;
      for (const menu of menus) {
        if (currentPath === menu.path) {
          found = menu;
          break;
        } else if (menu.submenus.data.length > 0) {
          for (const sub of menu.submenus.data) {
            if (currentPath === sub.path) {
              found = sub;
              break;
            }
          }
        }
      }
      if (!found) {
        // if not found, redirect to first found
        navigate(menus[0].submenus.data.length > 0 ? menus[0].submenus.data[0].path : menus[0].path, { replace: true });
        return;
      } else {
        // if current route is parent, redirect to the first children/sub
        if (route.children && route.children.length > 0 && found?.submenus?.data?.length > 0) {
          navigate(found.submenus.data[0].path, { replace: true });
          return;
        } else {
          setAllowed(true);
        }
      }
    }

    if (route.key) {
      fetchHelps(route.key);
    }
  }, [location, menus]);

  // Screen container for Playwright. Every screen is addressable by one name and
  // controls inside it are scoped through it, so `save-button` can stay `save-button`
  // on every screen. See docs/TESTIDS.md.
  const screenTestId = useMemo(() => {
    const matched = matchRoutes(router, location);
    const route = matched?.length ? matched[matched.length - 1].route : null;
    if (!route) return null;
    return route.testKey
      || route.key
      || (route.path || '').split('/').filter(Boolean).filter(seg => !seg.startsWith(':')).pop()
      || null;
  }, [location]);

  const handleLogout = async (e) => {
    e?.preventDefault();
    doLogout(dispatch);
  };

  const fetchHelps = async (key) => {
    try {
      const { data, status } = await api.get(`/help/pages/${key}?with_tooltips=true`);
      if (status === 200) {
        setHelps(data.tooltips);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const fetchLicenseHealth = useCallback(async (background = false) => {
    if (isLicenseHealthLoading.current) {
      return;
    }

    isLicenseHealthLoading.current = true;

    try {
      const { data, status } = await api.get('/common/license/state', { meta: { background } });
      if (status !== 200) {
        return;
      }

      const normalized = normalizeLicenseHealthPayload(data?.data);
      if (!normalized) {
        return;
      }

      persistLicenseState(normalized);
      setLicenseState(normalized);

      const configuredRefreshMinutes = Number(data?.data?.refresh_interval_minutes);
      if (Number.isFinite(configuredRefreshMinutes) && configuredRefreshMinutes > 0) {
        setLicenseHealthPollMs(Math.max(MIN_LICENSE_HEALTH_POLL_MS, configuredRefreshMinutes * 60 * 1000));
      }
    } catch (err) {
      if (err?.response?.status !== 402 && err?.response?.status !== 401) {
        Sentry.captureException(err);
      }
    } finally {
      isLicenseHealthLoading.current = false;
    }
  }, []);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    fetchLicenseHealth();
  }, [allowed, fetchLicenseHealth]);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const timer = setInterval(() => {
      fetchLicenseHealth(true);
    }, licenseHealthPollMs);

    return () => clearInterval(timer);
  }, [allowed, fetchLicenseHealth, licenseHealthPollMs]);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const onFocus = () => {
      fetchLicenseHealth(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchLicenseHealth(true);
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [allowed, fetchLicenseHealth]);

  if (!allowed) return <></>;

  const showLicenseWarning = licenseState?.status === 'warning';
  const licenseWarningMessage = licenseState?.warningMessage
    || (licenseState?.mode === 'airgapped'
      ? 'License warning detected in airgapped mode. Please renew and import the latest artifact.'
      : 'License warning detected. Please review and renew if needed.');

  return (
    <ErrorBoundary fallback={UserFallback} onError={onError}>
      <UserLayout testId={screenTestId}>
        {showLicenseWarning && (
          <div className="alert alert-warning d-flex align-items-start mb-4" role="alert">
            <i className="bi bi-exclamation-triangle-fill mr-2 mt-1" />
            <div>
              <div className="font-weight-bold">{editableTranslate('global.license.warning_title', 'Lizenzwarnung')}</div>
              <div>{editableTranslate(`global.license.warning_message.${licenseState.warningCode}`, licenseWarningMessage)}</div>
            </div>
          </div>
        )}

        <Outlet context={{ helps }} />

        {
          helps && helps.filter(h => h.short_text).map((help, i) => <ReactTooltip key={i} anchorSelect={`#help-${help.key}`} place="right-start" clickable>
            <div className="mb-2" dangerouslySetInnerHTML={{ __html: sanitize(help.short_text) }} />

            {
              help?.tooltip_linked_article && <Link to={`/help?key=${help.tooltip_linked_article?.hash_id}`} target="_blank">
                <Button variant="primary" size="sm" data-testid="learn-more-button">{editableTranslate('report.custom_learn_more')}</Button>
              </Link>
            }
          </ReactTooltip>)
        }

      </UserLayout>
    </ErrorBoundary>
  );
}
